import { useCallback, useEffect, useMemo, useState } from 'react'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import { client } from '../../hooks/Client'
import { useFirestoreQuery } from '../../firebase/useFirestore'
import useAllUpdateRequests from '../../hooks/updateRequests/useAllUpdateRequests'
import sortUpdateRequests from '../../helpers/updateRequests/sortUpdateRequests'
import formatRequestDate from '../../helpers/updateRequests/formatRequestDate'
import updateRequestStatusLabel, { UPDATE_REQUEST_STATUSES, UPDATE_REQUEST_STATUS_LABELS, UNKNOWN_STATUS_LABEL, isUpdateRequestStatus } from '../../helpers/updateRequests/updateRequestStatus'
import updateRequestSeverityLabel, { updateRequestSeverityRank } from '../../helpers/updateRequests/updateRequestSeverity'
import updateRequestListState from '../../helpers/updateRequests/updateRequestListState'
import Table from '../common/table/Table'
import TableCell from '../common/table/TableCell'
import Tooltip from '../common/Tooltip'
import StaticCardLabel from './StaticCardLabel'
import DashboardCardEmptyState from './DashboardCardEmptyState'
import UpdateRequestStatusModal from './UpdateRequestStatusModal'

// AI-CONTEXT: See docs/patterns.md#dashboard-static-card
// AI-CONTEXT: See docs/features/update-aanvragen.md

const DECCOS_MANAGEMENT_TENANT = '123423HJKHVKH'

const columns = [
    { key: 'request', label: 'VERZOEK' },
    { key: 'organisation', label: 'ORGANISATIE' },
    // Sorteerbaar, want dit is waar de kolom voor is: het blokkerende verzoek
    // bovenaan. sortRank zet een ontbrekende ernst onderaan in béide
    // richtingen — op de tekst 'Niet opgegeven' zou hij juist bovenaan komen.
    // AI-CONTEXT: See docs/pitfalls.md#ui-display-fallback-used-as-sort-key
    {
        key: 'severity',
        label: 'ERNST',
        searchable: false,
        sortable: true,
        sortAccessor: (request) => request?.Severity,
        sortRank: updateRequestSeverityRank,
    },
    { key: 'status', label: 'STATUS', searchable: false },
    { key: 'date', label: 'DATUM', searchable: false },
    { key: 'edit', label: '', searchable: false },
]

// Buiten de component: een nieuwe functie per render is een dependency van
// Table's filter-memo en laat het zoeken over alle rijen opnieuw draaien.
const searchAccessor = (request, names) => (
    `${request?.Title || ''} ${names.get(request?.CompagnyID) || ''}`
)

// Waarde in het statusfilter voor een status die niet klopt. Zo'n verzoek moet
// vindbaar blijven, anders verstopt het filter juist de dataschade.
const UNKNOWN_STATUS_FILTER = 'onbekend'

/**
 * Of een verzoek door het statusfilter komt.
 *
 * @param {{Status?: *}} request - Een UpdateRequests-document
 * @param {string} filter - Gekozen status, '' = alle, UNKNOWN_STATUS_FILTER = niet-kloppende status
 * @returns {boolean}
 */
const matchesStatusFilter = (request, filter) => {
    if (!filter) return true
    if (filter === UNKNOWN_STATUS_FILTER) return !isUpdateRequestStatus(request?.Status)
    return request?.Status === filter
}

/**
 * Alle updateverzoeken van alle organisaties, nieuwste bovenaan.
 *
 * Alleen zichtbaar voor de Deccos-beheertenant; de gate staat in
 * STATIC_CARD_VISIBILITY en wordt afgedwongen door de guardtest op de
 * Deccos-pill in staticCardRegistry.test.js.
 */
const DashboardUpdateRequestsCard = () => {

    // Firestore
    const isManagementTenant = client === DECCOS_MANAGEMENT_TENANT

    // De fout van de listener wordt opgevangen: een geweigerde lees geeft een
    // lege lijst, en 'Laden…' zou dan eeuwig blijven staan zonder te zeggen wat
    // er mis is. AI-CONTEXT: See docs/inbox.md Bug #133
    const [error, setError] = useState(/** @type {{code?: string}|null} */ (null))
    const onError = useCallback((queryError) => { setError(queryError) }, [])
    const requests = useAllUpdateRequests(isManagementTenant, onError)

    // Een geslaagde snapshot wist een eerdere fout; bij een mislukte listener
    // roept de engine setDocs niet aan, dus dit effect draait dan juist niet.
    useEffect(() => { if (Array.isArray(requests)) setError(null) }, [requests])

    // Welk verzoek er bewerkt wordt. Alleen het ID onthouden, niet het hele
    // document: een snapshot levert een nieuw object op en het venster zou dan
    // met verouderde inhoud blijven staan.
    // AI-CONTEXT: See docs/pitfalls.md#state-click-captured-firestore-snapshot
    const [editingId, setEditingId] = useState('')

    // '' = alle statussen.
    const [statusFilter, setStatusFilter] = useState('')

    // De naam van de organisatie staat niet op het verzoek. Eén lees over alle
    // omgevingen, daarna een opzoektabel — geen luisteraar per rij.
    // Geen orderBy: een CompagnyMeta-doc zonder Timestamp zou stil uit de query
    // vallen en die organisatie zou dan als '—' renderen, terwijl dat streepje
    // 'organisatie bestaat niet meer' hoort te betekenen. De volgorde wordt hier
    // toch niet gebruikt — er wordt een Map van gemaakt.
    // AI-CONTEXT: See docs/pitfalls.md#database-firestore-orderby-excludes-missing-field
    const allMeta = useFirestoreQuery(
        isManagementTenant ? 'CompagnyMeta' : null,
        { scope: 'none' },
    )

    const namesByTenant = useMemo(() => {
        const names = new Map()
        if (!Array.isArray(allMeta)) return names
        allMeta.forEach((meta) => {
            if (meta?.CompagnyID) names.set(meta.CompagnyID, meta.CommunityName || '')
        })
        return names
    }, [allMeta])

    const sorted = useMemo(() => sortUpdateRequests(requests), [requests])
    const shown = useMemo(
        () => sorted.filter((request) => matchesStatusFilter(request, statusFilter)),
        [sorted, statusFilter],
    )

    // 'Nog geen updateverzoeken' is een bewering; die mag pas als de gegevens
    // er echt zijn. Let op: de Table draagt met `emptyMessage` een TWEEDE lege
    // staat, dus die tak moet er ook achter.
    // AI-CONTEXT: See docs/pitfalls.md#ui-empty-state-claims-absence-while-loading
    const state = updateRequestListState({ docs: requests, error })
    const editing = sorted.find((request) => request.ID === editingId) || null

    // JSX
    return (
        <div>
            <StaticCardLabel
                title='Updateverzoeken'
                pill={{ variant: 'msi', label: 'Deccos' }}
            />
            {state === 'denied' ? (
                <p className='score-explain-card__placeholder'>
                    Geen toegang tot de updateverzoeken.
                </p>
            ) : state === 'failed' ? (
                <p className='score-explain-card__placeholder'>
                    We konden de updateverzoeken niet ophalen.
                </p>
            ) : state === 'loading' ? (
                <p className='score-explain-card__placeholder'>Laden…</p>
            ) : state === 'empty' ? (
                <DashboardCardEmptyState type='update-requests' />
            ) : (
                <Table
                    columns={columns}
                    items={shown}
                    getItemKey={(request) => request.ID}
                    searchAccessor={(request) => searchAccessor(request, namesByTenant)}
                    searchPlaceholder='Zoeken op verzoek of organisatie'
                    searchBarStart={(
                        <select
                            aria-label='Status'
                            title='Status'
                            className={`table-filter${statusFilter ? ' table-filter--active' : ''}`}
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                        >
                            <option value=''>Alle statussen</option>
                            {UPDATE_REQUEST_STATUSES.map((status) => (
                                <option key={status} value={status}>{UPDATE_REQUEST_STATUS_LABELS[status]}</option>
                            ))}
                            <option value={UNKNOWN_STATUS_FILTER}>{UNKNOWN_STATUS_LABEL}</option>
                        </select>
                    )}
                    emptyMessage='Geen updateverzoeken gevonden.'
                    renderRow={(request) => (
                        <>
                            <TableCell>{request.Title || 'Naamloos verzoek'}</TableCell>
                            {/* Een organisatie die inmiddels weg is mag het verzoek niet
                                laten verdwijnen — dan lijkt het alsof het nooit is
                                binnengekomen. */}
                            <TableCell>{namesByTenant.get(request.CompagnyID) || '—'}</TableCell>
                            <TableCell>{updateRequestSeverityLabel(request.Severity)}</TableCell>
                            <TableCell>{updateRequestStatusLabel(request.Status)}</TableCell>
                            <TableCell>{formatRequestDate(request.Timestamp) || 'Geen datum'}</TableCell>
                            <TableCell>
                                <Tooltip content='Stand van zaken bijwerken' top='-35px'>
                                    <EditOutlinedIcon
                                        data-id={request.ID}
                                        onClick={(e) => setEditingId(e.currentTarget.dataset.id)}
                                    />
                                </Tooltip>
                            </TableCell>
                        </>
                    )}
                />
            )}

            <UpdateRequestStatusModal
                modalOpen={Boolean(editing)}
                setModalOpen={(open) => { if (!open) setEditingId('') }}
                request={editing}
            />
        </div>
    )
}

export default DashboardUpdateRequestsCard
